<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'Dispatch_Instructions_Title' | translateWF: 'Dispatch Instructions' }}</h1>
        @if (canCreate()) {
          <div class="page__title_buttons">
            <p-speedDial [model]="speedDialItems" direction="left" [transitionDelay]="80">
              <ng-template #button let-toggleCallback="toggleCallback">
                <p-button (click)="toggleCallback($event)" [label]="'Add' | translateWF: 'Add'" icon="pi pi-plus" iconPos="left" />
              </ng-template>
              <ng-template #item let-item let-toggleCallback="toggleCallback">
                <p-button variant="outlined" severity="secondary" (click)="toggleCallback($event, item)" [label]="item.label | translateWF: item.label" />
              </ng-template>
            </p-speedDial>
          </div>
        }
      </div>
    </div>

    <div class="cs-page-list-grid">
      <!-- Filters Panel -->
      @if (filterConfig() && showFilters()) {
        <csl-dynamic-filters [filterConfig]="filterConfig()!" [state]="filterState" />
      }

      <!-- Data Table Panel -->
      <p-panel [header]="'Dispatch_Instructions' | translateWF: 'Dispatch Instructions'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-table #dt [value]="dispatchInstructions()" selectionMode="single" [rowHover]="true" [lazy]="true" [lazyLoadOnInit]="false" (onLazyLoad)="onTableSort($event)">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="number">{{ 'Dispatch_Number' | translateWF: 'Number' }} <p-sortIcon field="number" /></th>
                <th>{{ 'Dispatch_Invoice_Number' | translateWF: 'Invoice Number' }}</th>
                <th>{{ 'Dispatch_Type' | translateWF: 'Type' }}</th>
                <th>{{ 'Dispatch_Client' | translateWF: 'Client' }}</th>
                <th style="width: 12%">{{ 'Dispatch_Client_POs' | translateWF: 'Client POs' }}</th>
                <th>{{ 'Dispatch_Shipping_Address' | translateWF: 'Shipping Address' }}</th>
                <th>{{ 'Dispatch_Fabrics_By_Color' | translateWF: 'Fabrics by Color' }}</th>
                <th>{{ 'Dispatch_Mfr_Lot_No' | translateWF: 'Mfr. Lot No.' }}</th>
                <th>{{ 'Dispatch_Scheduled_Date' | translateWF: 'Scheduled Date' }}</th>
                <th>{{ 'Dispatch_Priority' | translateWF: 'Priority' }}</th>
                <th>{{ 'Dispatch_Entries' | translateWF: 'Entries' }}</th>
                <th>{{ 'Dispatch_Rolls' | translateWF: 'Rolls' }}</th>
                <th>{{ 'Dispatch_Status' | translateWF: 'Status' }}</th>
                <th>{{ 'Common_Created_Date' | translateWF: 'Created' }}</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-instruction>
              <tr class="cursor-pointer" (click)="redirectToDetail(instruction.id, instruction.type)">
                <td>
                  <a class="font-semibold text-primary">{{ instruction.number }}</a>
                </td>
                <td>
                  @if (instruction.invoiceNumber) {
                    {{ instruction.invoiceNumber }}
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>

                <td>
                  @if (getTypeLabel(instruction.type); as typeLabel) {
                    <p-tag [value]="typeLabel" severity="secondary" />
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (instruction.clientName) {
                    {{ instruction.clientName }}
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (instruction.clientPOs && instruction.clientPOs.length > 0) {
                    <div class="flex flex-wrap gap-1">
                      @for (po of instruction.clientPOs; track po) {
                        <p-tag [value]="po" severity="secondary" styleClass="text-sm px-2" />
                      }
                    </div>
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (instruction.shippingAddress) {
                    <span class="text-sm" [title]="instruction.shippingAddress">{{ truncateAddress(instruction.shippingAddress) }}</span>
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (hasFabricsByColor(instruction)) {
                    <div class="text-sm space-y-1">
                      @for (item of getFabricsByColor(instruction); track item.color) {
                        <div>
                          <strong>{{ item.color }}:</strong> {{ item.fabrics.join(', ') }}
                        </div>
                      }
                    </div>
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  @if (getManufacturerLots(instruction); as manufacturerLots) {
                    @if (manufacturerLots.length > 0) {
                      <div class="flex flex-wrap gap-1" [title]="manufacturerLots.join(', ')">
                        @for (manufacturerLot of manufacturerLots.slice(0, maxVisibleManufacturerLots); track manufacturerLot) {
                          <p-tag [value]="manufacturerLot" severity="secondary" styleClass="text-sm px-2" />
                        }
                        @if (manufacturerLots.length > maxVisibleManufacturerLots) {
                          <p-tag [value]="'+' + (manufacturerLots.length - maxVisibleManufacturerLots)" severity="contrast" styleClass="text-sm px-2" />
                        }
                      </div>
                    } @else {
                      <span class="text-gray-400">-</span>
                    }
                  }
                </td>
                <td>
                  {{ formatScheduledDate(instruction.dateAndPriorityKey?.date) }}
                </td>
                <td>
                  @if (instruction.dateAndPriorityKey?.priority !== undefined && instruction.dateAndPriorityKey?.priority !== null) {
                    <p-tag severity="warn" [value]="'#' + instruction.dateAndPriorityKey.priority" />
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
                <td>
                  <p-tag severity="info" [value]="getLineCount(instruction).toString()" />
                </td>
                <td>
                  <p-tag severity="secondary" [value]="getRollCount(instruction).toString()" />
                </td>
                <td>
                  <p-tag [value]="getStatusLabel(instruction.status)" [severity]="getStatusSeverity(instruction.status)" />
                </td>
                <td>
                  @if (instruction.createdDate) {
                    {{ instruction.createdDate | date: 'short' }}
                  } @else {
                    <span class="text-gray-400">-</span>
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="14" class="text-center py-8">
                  {{ 'Dispatch_Instructions_Not_Found' | translateWF: 'No dispatch instructions found' }}
                </td>
              </tr>
            </ng-template>
          </p-table>

          <p-paginator
            [rows]="pageSize"
            [totalRecords]="totalRecords"
            [first]="(currentPage - 1) * pageSize"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[10, 25, 50, 100]"
            class="mt-4"
          />
        </p-card>
      </p-panel>
    </div>
  </div>
</main>

<p-toast position="bottom-right" key="br" />
